// App shell: hash routes, top bar with connection/queue state, sign-in gates (first setup, login,
// forced password change / 2FA setup, another user's data on this device), recovery of unsaved
// recordings, global toasts and dialogs.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { featuresForSession, useFeatures, useOutboxStatus, useOwnership, useStorageWarning } from './lib/hooks';
import { buildVersion, isOutdated, refreshApp } from './lib/version';
import { navigate, useRoute, type Route } from './lib/router';
import { installSessionGate } from './lib/session';
import { ContentHidden } from './components/ContentHidden';
import { DialogHost, useModalLayer } from './components/Dialog';
import { Icon } from './components/Icon';
import { installNavigationGuard, useRecordingActive } from './components/NavGuard';
import { dismissRecoveryCodes, RecoveryCodesView, usePendingRecoveryCodes } from './components/RecoveryCodes';
import { RecoveryManager } from './components/RecoveryDialog';
import { AdminResetNotice } from './components/AdminResetNotice';
import { dismissAllToasts, dismissPrivateToasts, ToastHost } from './components/Toasts';
import { TopBar } from './components/TopBar';
import { AccountSetupPage, finishTotpSetup } from './pages/AccountSetup';
import { ForeignDataPage } from './pages/ForeignData';
import { HomePage } from './pages/Home';
import { InspectionPage } from './pages/Inspection';
import { LoginPage } from './pages/Login';
import { MeetingPage } from './pages/Meeting';
import { closeReminderNotifications, NotesPage } from './pages/Notes';
import { QuickStart } from './pages/QuickStart';
import { NotFoundPage, PageLoading } from './pages/NotFound';
import { SettingsPage } from './pages/Settings';
import { SetupPage } from './pages/Setup';

// Before React mounts: the guard must see `hashchange` before useRoute() does.
installNavigationGuard();
// Queued changes are only sent once this tab knows who is signed in (see session.ts).
installSessionGate(featuresForSession);

const APP_NAME = 'BAAM Transkripcija';
/** The only help when the browser storage does not answer (see localdb: slow open). */
const STORAGE_RESTART_ADVICE = 'Če se to ne popravi v nekaj sekundah, zaprite brskalnik (aplikacijo) in ga znova odprite.';

const PAGE_TITLE: Record<Route['name'], string> = {
  home: APP_NAME,
  meeting: `Sestanek · ${APP_NAME}`,
  inspection: `Nadzor · ${APP_NAME}`,
  notes: `Opomniki · ${APP_NAME}`,
  settings: `Nastavitve · ${APP_NAME}`,
  login: `Prijava · ${APP_NAME}`,
  quick: APP_NAME,
  notFound: APP_NAME,
};

function pageKey(route: Route): string {
  return route.name === 'meeting' || route.name === 'inspection' ? `${route.name}:${route.id}` : route.name;
}

/**
 * Pages kept mounted (showing nothing of their content) under the login when the session ends:
 * they may hold a running recording or typed text. Others are unmounted meanwhile.
 */
const KEPT_UNDER_LOGIN: ReadonlySet<Route['name']> = new Set(['meeting', 'inspection', 'notes']);

function renderRoute(route: Route): ReactNode {
  switch (route.name) {
    case 'home':
      return <HomePage />;
    case 'meeting':
      return <MeetingPage key={route.id} id={route.id} />;
    case 'inspection':
      return <InspectionPage key={route.id} id={route.id} />;
    case 'notes':
      return <NotesPage expandedId={route.id} />;
    case 'settings':
      return <SettingsPage />;
    case 'login':
      // Shown only for a moment: the effect in App redirects home when no login is needed.
      return <PageLoading />;
    case 'quick':
      return <QuickStart mode={route.mode} />;
    case 'notFound':
      return <NotFoundPage />;
  }
}

function Banner({ tone, children, action }: { tone: 'warning' | 'error'; children: ReactNode; action?: ReactNode }) {
  return (
    <div className={`banner banner--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <div className="banner__inner">
        <Icon name="alert" size={18} />
        <span className="banner__text">{children}</span>
        {action}
      </div>
    </div>
  );
}

/** The server was updated while this bundle is still the old one: offer the refresh. */
function UpdateBanner({ serverVersion }: { serverVersion: string | undefined }) {
  const [busy, setBusy] = useState(false);
  if (!isOutdated(buildVersion(), serverVersion)) return null;
  return (
    <Banner
      tone="warning"
      action={
        <button
          type="button"
          className="btn btn--secondary btn--small banner__action"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void refreshApp();
          }}
        >
          <Icon name={busy ? 'spinner' : 'refresh'} size={18} /> Osveži
        </button>
      }
    >
      Na strežniku je nova različica programa ({serverVersion}). Osvežite stran, da jo uporabite.
    </Banner>
  );
}

/**
 * A sign-in screen over the open page: a session that expires (or a changed password, or 2FA
 * becoming mandatory) must not unmount a running recording, an open draft or unsaved text. The
 * page underneath is inert; under the login it shows none of its content (see ContentHidden).
 */
function AuthOverlay({ labelledBy, label, children }: { labelledBy?: string; label?: string; children: ReactNode }) {
  useModalLayer(true);
  // Layout effect: runs before the form takes the focus.
  useLayoutEffect(() => {
    const previous = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    return () => {
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);
  return (
    <div className="login-overlay" role="dialog" aria-modal="true" aria-labelledby={labelledBy} aria-label={labelledBy ? undefined : label}>
      {children}
    </div>
  );
}

interface ShownUser {
  id: string;
  username: string;
}

export function App() {
  const route = useRoute();
  const features = useFeatures();
  const outbox = useOutboxStatus();
  const storageWarning = useStorageWarning();
  const recordingActive = useRecordingActive();
  const mainRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);

  const f = features.data;
  const user = f?.authenticated ? f.user : null;
  const pendingCodes = usePendingRecoveryCodes(user?.id ?? null);
  const ownership = useOwnership(user);
  const needSetup = f !== null && f.setupRequired && !user;
  const needLogin = !needSetup && (outbox.authRequired || (f !== null && f.authRequired && !f.authenticated));
  const restricted = !needLogin && user !== null && f !== null && f.restricted;
  const ownershipOk = ownership.state.status === 'ok';
  const key = pageKey(route);

  // Once the content has been shown, a later sign-in screen is an overlay (see AuthOverlay). The
  // flag is state rather than derived: login() clears authRequired before the features are
  // re-read, and that moment must not unmount the page either.
  const [hadSession, setHadSession] = useState(false);
  /** The user whose pages were shown last (another person must never see them). */
  const [shownUser, setShownUser] = useState<ShownUser | null>(null);
  /** Counts sign-ins of another user over a shown page (each one starts at home). */
  const [switches, setSwitches] = useState(0);
  const switchedUser = user !== null && shownUser !== null && user.id !== shownUser.id;
  if (switchedUser) {
    // Someone else signed in over the old page: drop it, start fresh.
    setShownUser(null);
    setSwitches((n) => n + 1);
    if (hadSession) setHadSession(false);
  }
  const contentAllowed = f !== null && !needSetup && !needLogin && !restricted && ownershipOk && pendingCodes === null && !switchedUser;
  if (contentAllowed && !hadSession) setHadSession(true);
  if (contentAllowed && user && shownUser?.id !== user.id) setShownUser({ id: user.id, username: user.username });
  // An explicit logout (Settings navigates to the login route) shows the plain login page.
  if (hadSession && needLogin && route.name === 'login') setHadSession(false);
  const overlayOk = hadSession && !switchedUser && f !== null;
  const loginOverlay = overlayOk && needLogin;
  // The recovery codes come first: enabling 2FA lifts the restriction only once they were confirmed.
  const codesOverlay = overlayOk && !needLogin && pendingCodes !== null;
  const restrictedOverlay = overlayOk && !needLogin && restricted && pendingCodes === null;
  const anyOverlay = loginOverlay || restrictedOverlay || codesOverlay;

  useEffect(() => {
    if (switches > 0) navigate({ name: 'home' }, { replace: true });
  }, [switches]);

  // Nobody is signed in: codes still waiting for confirmation are gone for good.
  const signedOut = f !== null && !f.authenticated;
  useEffect(() => {
    if (signedOut) dismissRecoveryCodes();
  }, [signedOut]);

  // The session ended (here, in another tab or on the server): messages that show the user's
  // content, such as a due reminder's text, must not stay above the login, nor the reminders'
  // system notifications in the notification shade.
  useEffect(() => {
    if (!needLogin && !signedOut) return;
    dismissPrivateToasts();
    void closeReminderNotifications();
  }, [needLogin, signedOut]);

  // Someone else signed in: messages about the previous user's work go away (an expired session
  // keeps them: the same person usually signs in again).
  const userId = user?.id ?? null;
  const previousUserId = useRef<string | null>(null);
  useEffect(() => {
    if (userId === null) return;
    if (previousUserId.current !== null && previousUserId.current !== userId) dismissAllToasts();
    previousUserId.current = userId;
  }, [userId]);

  useEffect(() => {
    document.title = needSetup
      ? `Prva nastavitev · ${APP_NAME}`
      : needLogin
        ? PAGE_TITLE.login
        : restricted
          ? `Varnost računa · ${APP_NAME}`
          : PAGE_TITLE[route.name];
  }, [route.name, needLogin, needSetup, restricted]);

  // New page: start at the top and move keyboard/screen reader focus into the content.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo(0, 0);
    if (document.activeElement === document.body || document.activeElement === null) {
      mainRef.current?.focus({ preventScroll: true });
    }
  }, [key]);

  // Not while the features are being re-read: right after a logout they still say "authenticated".
  const featuresLoading = features.loading;
  useEffect(() => {
    if (route.name === 'login' && f && !needLogin && !needSetup && !featuresLoading) navigate({ name: 'home' }, { replace: true });
  }, [route.name, f, needLogin, needSetup, featuresLoading]);

  const codesView = pendingCodes && user && (
    <RecoveryCodesView
      page
      codes={pendingCodes.codes}
      username={pendingCodes.username}
      onDone={() => {
        dismissRecoveryCodes();
        finishTotpSetup(user);
      }}
    />
  );

  // Always inside the same wrapper, so the page keeps its state when the login comes and goes.
  const page = (
    <ContentHidden hidden={loginOverlay}>{loginOverlay && !KEPT_UNDER_LOGIN.has(route.name) ? null : renderRoute(route)}</ContentHidden>
  );

  let content: ReactNode;
  if (!f) {
    content = features.error ? (
      <div className="page page--center">
        <div className="card empty-card">
          <Icon name="cloudOff" size={36} className="empty-card__icon" />
          <h1 className="empty-card__title">Strežnik ni dosegljiv</h1>
          <p className="muted">{features.error}</p>
          <div className="empty-card__actions">
            <button type="button" className="btn btn--primary" onClick={features.reload}>
              <Icon name="refresh" size={20} /> Poskusi znova
            </button>
          </div>
        </div>
      </div>
    ) : (
      <PageLoading />
    );
  } else if (needSetup) {
    content = <SetupPage />;
  } else if (needLogin && !loginOverlay) {
    content = (
      <LoginPage
        expired={outbox.authRequired}
        onSuccess={() => {
          if (route.name === 'login') navigate({ name: 'home' }, { replace: true });
        }}
      />
    );
  } else if (anyOverlay) {
    content = page;
  } else if (ownership.state.status === 'checking' && ownership.state.failed) {
    content = (
      <div className="page page--center">
        <div className="card empty-card" role="status">
          <Icon name="storage" size={36} className="empty-card__icon" />
          <h1 className="empty-card__title">Preverjam podatke v tej napravi</h1>
          <p className="muted">
            Shramba brskalnika se trenutno ne odziva. Dokler ne preverimo, ali so v tej napravi neposlani podatki drugega uporabnika, ne
            prikažemo in ne pošljemo ničesar. Poskušamo znova …
          </p>
          <p className="muted">{STORAGE_RESTART_ADVICE}</p>
          <div className="empty-card__actions">
            <button type="button" className="btn btn--primary" onClick={ownership.recheck}>
              <Icon name="refresh" size={20} /> Poskusi znova
            </button>
          </div>
        </div>
      </div>
    );
  } else if (ownership.state.status === 'checking' || switchedUser) {
    content = <PageLoading />;
  } else if (ownership.state.status === 'foreign' && user) {
    content = <ForeignDataPage user={user} previous={ownership.state.previous} work={ownership.state.work} onResolved={ownership.recheck} />;
  } else if (pendingCodes && user) {
    content = codesView;
  } else if (restricted && user) {
    content = <AccountSetupPage user={user} />;
  } else {
    content = page;
  }

  const insecure = typeof window !== 'undefined' && window.isSecureContext === false;

  return (
    <div className="app">
      <TopBar status={outbox} minimal={!contentAllowed} inert={anyOverlay} user={loginOverlay ? null : user} />
      {insecure && (
        <Banner tone="error">Stran ni odprta prek varne povezave (HTTPS). Snemanje ne bo delovalo; uporabite naslov, ki se začne s https://.</Banner>
      )}
      {storageWarning && <Banner tone="warning">{storageWarning}</Banner>}
      <UpdateBanner serverVersion={features.data?.version} />
      <main ref={mainRef} className="main" tabIndex={-1} inert={anyOverlay}>
        {content}
      </main>
      {loginOverlay && (
        <AuthOverlay labelledBy="login-title">
          {recordingActive && (
            <div className="notice notice--info login-overlay__notice" role="status">
              <Icon name="mic" size={20} className="notice__icon" />
              <p className="notice__content">Snemanje se nadaljuje v tej napravi. Prijavite se, da ga lahko končate in shranite.</p>
            </div>
          )}
          <LoginPage expired overlay defaultUsername={shownUser?.username} />
        </AuthOverlay>
      )}
      {restrictedOverlay && user && (
        <AuthOverlay label="Varnost računa">
          <AccountSetupPage user={user} />
        </AuthOverlay>
      )}
      {codesOverlay && <AuthOverlay label="Obnovitvene kode">{codesView}</AuthOverlay>}
      {contentAllowed && <RecoveryManager />}
      {/* After an administrator reset this account's password / 2FA: shown until acknowledged. */}
      {contentAllowed && user && <AdminResetNotice key={user.id} />}
      <ToastHost />
      <DialogHost />
    </div>
  );
}
